/* eslint-disable */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Check,
  Pencil,
} from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import { PageHeader } from "@shared/components/PageHeader";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { ActivateDialog } from "./activateDialogue";
import { useGetChoices } from "@features/ix/hooks";

interface ReviewStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  totalSteps?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  onGoToStep?: (stepIndex: number) => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  stepHelpers,
  currentStepIndex = 2,
  totalSteps = 3,
  onPrevious,
  onGoToStep,
  setValidationError,
  clearValidationError,
}) => {
  const navigate = useNavigate();
  const { data: directionData } = useGetChoices("direction");
  const { data: syncTypeData } = useGetChoices("schedule_type");

  const directionChoices = useMemo(
    () =>
      directionData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [directionData],
  );

  const syncTypeChoices = useMemo(
    () =>
      syncTypeData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [syncTypeData],
  );

  const [isActivating, setIsActivating] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // States to hold compiled config
  const [connectionData, setConnectionData] = useState<any>({});
  const [syncData, setSyncData] = useState<any>({});

  const dataLoadedRef = useRef(false);

  // Load saved data from all previous steps
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const step0 = stepHelpers.getStepData(0) || {}; // Connection
        const step1 = stepHelpers.getStepData(1) || {}; // Sync Flows

        setConnectionData(step0);
        setSyncData(step1);

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load review step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers]);

  // Dynamically load active sync flows from step data
  const displayFlows = useMemo(() => {
    const flows = syncData.flows || [];
    return flows.filter((f: any) => f.isActive);
  }, [syncData.flows]);

  const handleActivate = async () => {
    setIsActivating(true);
    if (clearValidationError) {
      clearValidationError();
    }

    try {
      // Save activation state in localStorage so the landing page reflects connection status
      localStorage.setItem("neptune_connected", "true");

      // Save connection configuration to simulate persistent settings
      const finalConfig = {
        connection: connectionData,
        syncFlows: syncData,
        activatedAt: new Date().toISOString(),
      };
      localStorage.setItem("neptune_config", JSON.stringify(finalConfig));

      // Reset the stepper data
      if (stepHelpers) {
        stepHelpers.resetAllData();
      }

      toast({
        title: "Integration Activated",
        description: "Neptune ERP integration has been successfully activated.",
      });

      // Redirect back to ERP Landing page
      setTimeout(() => {
        setIsDialogOpen(false);
        navigate("/integration-hub/erp-integration");
      }, 1000);
    } catch (error) {
      console.error("Failed to activate integration:", error);
      if (setValidationError) {
        setValidationError("Failed to activate integration. Please try again.");
      }
      setIsActivating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <PageHeader
          title="Review & Activate"
          subtitle="Final summary. Once activated, syncing begins immediately."
        />
        <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-200 text-[12px] font-medium py-1 px-3 rounded-full shrink-0">
          Pre-flight checks: passed
        </Badge>
      </div>

      {/* Overview Success Banner */}
      <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-4 flex gap-3 items-start">
        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0 mt-0.5">
          <Check className="w-3 h-3" strokeWidth={3} />
        </div>
        <div className="space-y-0.5">
          <h3 className="text-sm font-bold text-emerald-800 leading-normal">
            All checks passed — ready to activate
          </h3>
          <p className="text-xs text-emerald-650 font-semibold leading-relaxed">
            Connection verified · {displayFlows.length} active flows
          </p>
        </div>
      </div>

      {/* Grid of Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Card 1: Connection */}
        <div className="p-6 border border-gray-200 rounded-2xl bg-white flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h2 className="text-base font-bold text-gray-900">Connection</h2>
            <Button
              variant="ghost"
              onClick={() => onGoToStep?.(0)}
              className="h-8 px-2 text-[#5850ec] hover:text-[#4f46e5] font-semibold text-xs gap-1.5 flex items-center hover:bg-transparent"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span>Edit</span>
            </Button>
          </div>
          <div className="flex-1 space-y-3">
            <ValueLabel
              label="Name"
              value={
                <span className="font-bold text-gray-900">
                  {connectionData.integrationName || "NA"}
                </span>
              }
              layout="horizontal"
              className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
            />
            <ValueLabel
              label="Environment"
              value={
                <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 border border-amber-200 text-[10px] font-bold py-0.5 px-2 rounded-full capitalize">
                  {connectionData.integrationName ? "production" : "NA"}
                </Badge>
              }
              layout="horizontal"
              className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
            />
            <ValueLabel
              label="Auth"
              value={
                <span className="font-mono text-xs text-gray-700">
                  {Number(connectionData.authType) === 1
                    ? "api_key"
                    : Number(connectionData.authType) === 0
                      ? "oauth2"
                      : Number(connectionData.authType) === 3
                        ? "basic"
                        : connectionData.authType || "NA"}
                </span>
              }
              layout="horizontal"
              className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
            />
            {Number(connectionData.authType) === 1 && (
              <ValueLabel
                label="API Key / Token"
                value={
                  <span className="font-mono text-[11px] text-gray-700 break-all text-right">
                    {connectionData.apiKey ? "••••••••••••" : "NA"}
                  </span>
                }
                layout="horizontal"
                className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
              />
            )}
            {Number(connectionData.authType) === 0 && (
              <>
                <ValueLabel
                  label="Client ID"
                  value={
                    <span className="font-mono text-[11px] text-gray-700 break-all text-right">
                      {connectionData.clientId || "NA"}
                    </span>
                  }
                  layout="horizontal"
                  className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
                />
                <ValueLabel
                  label="Secret Key"
                  value={
                    <span className="font-mono text-[11px] text-gray-700 break-all text-right">
                      {connectionData.clientSecret ? "••••••••••••" : "NA"}
                    </span>
                  }
                  layout="horizontal"
                  className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
                />
                <ValueLabel
                  label="Token URL"
                  value={
                    <span className="font-mono text-[11px] text-gray-700 break-all text-right">
                      {connectionData.tokenUrl || "NA"}
                    </span>
                  }
                  layout="horizontal"
                  className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
                />
              </>
            )}
            {Number(connectionData.authType) === 3 && (
              <>
                <ValueLabel
                  label="Username"
                  value={
                    <span className="font-mono text-[11px] text-gray-700 break-all text-right">
                      {connectionData.username || "NA"}
                    </span>
                  }
                  layout="horizontal"
                  className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
                />
                <ValueLabel
                  label="Password"
                  value={
                    <span className="font-mono text-[11px] text-gray-700 break-all text-right">
                      {connectionData.password ? "••••••••••••" : "NA"}
                    </span>
                  }
                  layout="horizontal"
                  className="justify-between w-full py-1.5 border-b border-gray-100/50 last:border-0"
                />
              </>
            )}
          </div>
        </div>

        {/* Card 2: Sync Flows */}
        <div className="p-6 border border-gray-200 rounded-2xl bg-white flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h2 className="text-base font-bold text-gray-900">
              Sync Flows · {displayFlows.length}
            </h2>
            <Button
              variant="ghost"
              onClick={() => onGoToStep?.(1)}
              className="h-8 px-2 text-[#5850ec] hover:text-[#4f46e5] font-semibold text-xs gap-1.5 flex items-center hover:bg-transparent"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span>Edit</span>
            </Button>
          </div>
          <div className="flex-1 space-y-3">
            {displayFlows.length > 0 ? (
              displayFlows.map((flow: any, index: number) => (
                <div
                  key={flow.endpointId || index}
                  className="flex items-center justify-between py-1.5 border-b border-gray-100/50 last:border-0"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-gray-900 text-sm">
                      {flow.objectType}
                    </span>
                    <span className="text-[11px] text-gray-400 font-medium">
                      ·{" "}
                      {flow.syncType === "scheduled"
                        ? (
                            syncTypeChoices.find(
                              (opt) =>
                                Number(opt.value) ===
                                Number(flow.scheduleLabel),
                            )?.label || "scheduled"
                          )?.toLowerCase()
                        : "real-time"}
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[10px] text-gray-650 font-bold border border-gray-200 rounded px-2 py-0.5 bg-white shrink-0"
                  >
                    {(() => {
                      const directionKey = Number(flow.directionLabel);
                      if (directionKey === 2) return "⇆ Bi-dir";
                      if (directionKey === 0) return "→ Neptune";
                      if (directionKey === 1) return "← Neptune";
                      const label = directionChoices.find(
                        (opt) => Number(opt.value) === directionKey,
                      )?.label;
                      return label || String(flow.directionLabel);
                    })()}
                  </Badge>
                </div>
              ))
            ) : (
              <div className="text-gray-400 italic text-xs py-2">
                No active sync flows configured
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <div className="flex items-center gap-3 py-4 border-t border-gray-100 mt-6">
        <input
          id="agree-checkbox"
          type="checkbox"
          checked={isAgreed}
          onChange={(e) => setIsAgreed(e.target.checked)}
          className="h-4.5 w-4.5 rounded border-gray-300 text-[#5850ec] focus:ring-[#5850ec] cursor-pointer"
        />
        <label
          htmlFor="agree-checkbox"
          className="text-xs sm:text-sm font-semibold text-gray-700 cursor-pointer select-none leading-none"
        >
          I understand activating will start syncing live data with Neptune
          production.
        </label>
      </div>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-6 border-t mt-4">
        <Button
          onClick={onPrevious}
          variant="outline"
          disabled={isActivating}
          className="h-10 px-5 rounded-xl border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold gap-2 flex items-center"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Back</span>
        </Button>
        <div className="flex items-center gap-4">
          <Button
            onClick={() => setIsDialogOpen(true)}
            disabled={isActivating || !isAgreed}
            variant="default"
            className="h-10 px-5 rounded-xl font-bold bg-[#5850ec] hover:bg-[#4f46e5] text-white gap-2 flex items-center shadow-sm disabled:opacity-50"
          >
            {isActivating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Activating...</span>
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 text-white fill-white" />
                <span>Activate Integration</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Confirmation Activation Dialogue */}
      <ActivateDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleActivate}
        isActivating={isActivating}
        syncFlowsCount={displayFlows.length}
      />
    </div>
  );
};

export default ReviewStep;
